import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import DescriptorT from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { descriptorDimensions, showPricesButtonText } from '@/utils/rapidstor';

import {
    descriptorTitle,
    isDescriptorAvailable,
    sizeLabel,
    sortUnitTypes,
} from '../../features/rapidstor/helpers';

import classes from './styles.module.scss';

import IconArrowRight from '../../assets/icon-arrow-right.svg?react';
import Button from '../elements/button';
import DescriptorLabel from './label';
import DescriptorTag from './tag';

interface Props extends React.HTMLAttributes<HTMLElement> {
    descriptor: DescriptorT;
    carousel: boolean;
    active?: boolean;
    onSelect: () => void;
}

function Descriptor({ descriptor, onSelect, carousel, active }: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();

    const sortedUnitTypes = sortUnitTypes(descriptor.unitTypes, location);

    const descriptorTitleClasses: Record<string | number, string> = {
        0: classes.descriptorTitleLabel,
        1: classes.descriptorAreaLabel,
        2: classes.descriptorDimensionsLabel,
    };

    const buttonText = useMemo(() => {
        if (!isDescriptorAvailable(descriptor)) {
            return location.rapidstor.noAvailableUnitsText;
        }
        if (location.rapidstor.hidePrices) {
            return showPricesButtonText(location, true);
        } else {
            return t('cta:continue');
        }
    }, [descriptor, location, t]);

    return (
        <div className={`${carousel ? classes.carousel_descriptor : classes.descriptor}`}>
            <div className={`${classes.header}`}>
                <div className={classes.left}>
                    <DescriptorLabel
                        descriptor={descriptor}
                        unitType={sortedUnitTypes[0]}
                    />
                </div>

                <div className={classes.right}>
                    <Button
                        onclick={onSelect}
                        type='primary'
                        size='small'
                        testId='descriptor-button'
                        id='continue_button'
                    >
                        {buttonText}
                    </Button>
                </div>
            </div>
            <div className={classes.icon}>
                <img
                    src={descriptor.picture}
                    style={{
                        opacity: isDescriptorAvailable(descriptor) ? 1 : 0.5,
                    }}
                />
            </div>
            <div
                className={`${classes.active}`}
                style={{
                    display: carousel && active ? 'flex' : 'none',
                }}
            >
                <div className={classes.stats}>
                    <span
                        className={`${descriptorTitleClasses[
                            descriptorTitle(descriptor, location)[0]
                            ]
                            }`}
                    >
                        {descriptorTitle(descriptor, location)[1]}
                    </span>
                    <span hidden={descriptorTitle(descriptor, location)[0] === 0}>
                        {sizeLabel(descriptor.area, location)}
                    </span>
                </div>
                <span hidden={descriptorTitle(descriptor, location)[0] === 2}>
                    {descriptorDimensions(sortedUnitTypes[0], location, true)}
                </span>
            </div>
            <div className={carousel ? classes.hidden : classes.desktop_info}>
                <div className={`${classes.desktop_header}`}>
                    <div className={classes.left}>
                        <div className={classes.stats}>
                            <span
                                className={`${descriptorTitleClasses[
                                    descriptorTitle(descriptor, location)[0]
                                    ]
                                    }`}
                            >
                                {descriptorTitle(descriptor, location)[1]}
                            </span>
                            <span hidden={descriptorTitle(descriptor, location)[0] === 0}>
                                {sizeLabel(descriptor.area, location)}
                            </span>
                        </div>
                        <span hidden={descriptorTitle(descriptor, location)[0] === 2}>
                            {descriptorDimensions(sortedUnitTypes[0], location, true)}
                        </span>
                    </div>

                    <div className={classes.right}>
                        <Button
                            onclick={onSelect}
                            type='primary'
                            size='small'
                            extClasses={`${classes.desktop_button}`}
                            testId='descriptor-button'
                            id='continue_button'
                        >
                            {buttonText}
                            <IconArrowRight className={classes.arrow} />
                        </Button>
                    </div>
                </div>
                <div className={classes.desktop_body}>
                    <span>{descriptor.specialText}</span>
                    <p>{descriptor.description}</p>
                    {descriptor.tags && descriptor.tags.length > 0 && (
                        <>
                            <span className={`${classes.ideal_for}`}>
                                {t('descriptor:idealFor')}
                            </span>
                            <DescriptorTag tags={descriptor.tags} />
                        </>
                    )}
                </div>
            </div>
            <div className={classes.footer}>
                <span>{descriptor.specialText}</span>
                <span>{descriptor.description}</span>
            </div>
        </div>
    );
}

export default Descriptor;
